import { createMapBounds, getLongitudeSpan, normaliseLongitude } from './bounds'
import type { LngLat, MapBounds } from './types'

const TILE_SIZE_PX = 512
const DEFAULT_FIT_PADDING_PX = 48
const MAX_PADDING_RATIO = 0.15
const MIN_AVAILABLE_PX = 32
const MERCATOR_LATITUDE_LIMIT = 85.051129
/** Leaves closer than roughly a metre can never be pulled apart by zooming. */
const DEGENERATE_SPAN_DEGREES = 1e-5
/**
 * Supercluster splits a cluster once `Math.floor(zoom)` reaches its expansion
 * zoom, so landing a fraction below the integer (mapbox camera easing is not
 * bit-exact) leaves the cluster intact and the click looks ignored.
 */
const EXPANSION_ZOOM_EPSILON = 0.01

interface ClusterLeafFeature {
  geometry: { coordinates: Array<number> }
}

export interface ClusterExpansionIndex {
  getClusterExpansionZoom(clusterId: number): number
  getLeaves(clusterId: number, limit?: number, offset?: number): Array<ClusterLeafFeature>
}

export interface ClusterExpansionViewport {
  width: number
  height: number
}

interface GetClusterExpansionTargetArgs {
  index: ClusterExpansionIndex
  clusterId: number
  /** Cluster centroid, used when the leaves cannot be resolved. */
  fallbackCoordinate: LngLat
  currentZoom: number
  maxZoom: number
  viewport?: ClusterExpansionViewport | null
  paddingPx?: number
}

export interface ClusterExpansionTarget {
  center: LngLat
  zoom: number
}

function latitudeToMercatorY(latitude: number): number {
  const clamped = Math.min(Math.max(latitude, -MERCATOR_LATITUDE_LIMIT), MERCATOR_LATITUDE_LIMIT)
  const sin = Math.sin(clamped * Math.PI / 180)

  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)
}

function mercatorYToLatitude(y: number): number {
  return (2 * Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) - Math.PI / 2) * 180 / Math.PI
}

function getBoundsCenter(bounds: MapBounds): LngLat {
  const northY = latitudeToMercatorY(bounds.north)
  const southY = latitudeToMercatorY(bounds.south)

  return {
    lat: mercatorYToLatitude((northY + southY) / 2),
    lng: normaliseLongitude(bounds.west + getLongitudeSpan(bounds.east, bounds.west) / 2),
  }
}

/** Zoom at which `bounds` fills the viewport, minus padding. `Infinity` when the bounds have no span. */
function getBoundsFitZoom(
  bounds: MapBounds,
  viewport: ClusterExpansionViewport,
  paddingPx: number,
): number {
  const padding = Math.min(
    paddingPx,
    viewport.width * MAX_PADDING_RATIO,
    viewport.height * MAX_PADDING_RATIO,
  )
  const availableWidth = Math.max(viewport.width - padding * 2, MIN_AVAILABLE_PX)
  const availableHeight = Math.max(viewport.height - padding * 2, MIN_AVAILABLE_PX)

  const worldWidthFraction = getLongitudeSpan(bounds.east, bounds.west) / 360
  const worldHeightFraction = Math.abs(
    latitudeToMercatorY(bounds.north) - latitudeToMercatorY(bounds.south),
  )

  const zoomForWidth = worldWidthFraction > 0
    ? Math.log2(availableWidth / (worldWidthFraction * TILE_SIZE_PX))
    : Infinity
  const zoomForHeight = worldHeightFraction > 0
    ? Math.log2(availableHeight / (worldHeightFraction * TILE_SIZE_PX))
    : Infinity

  return Math.min(zoomForWidth, zoomForHeight)
}

function getLeafCoordinates(index: ClusterExpansionIndex, clusterId: number): Array<LngLat> {
  try {
    return index.getLeaves(clusterId, Infinity)
      .map(({ geometry }) => ({ lng: geometry.coordinates[0], lat: geometry.coordinates[1] }))
      .filter(({ lng, lat }) => Number.isFinite(lng) && Number.isFinite(lat))
  } catch {
    return []
  }
}

/**
 * Resolves the camera a cluster should move to when tapped.
 *
 * Supercluster's expansion zoom only guarantees the cluster breaks into its
 * immediate children — which are frequently clusters themselves, and which sit
 * barely a cluster radius apart even when they are not. That is what makes a
 * single tap feel like it did nothing. Framing the cluster's leaves instead
 * separates them in one go, with the expansion zoom kept as a lower bound so a
 * tap always makes progress.
 */
export function getClusterExpansionTarget({
  index,
  clusterId,
  fallbackCoordinate,
  currentZoom,
  maxZoom,
  viewport,
  paddingPx = DEFAULT_FIT_PADDING_PX,
}: GetClusterExpansionTargetArgs): ClusterExpansionTarget {
  const expansionZoom = index.getClusterExpansionZoom(clusterId) + EXPANSION_ZOOM_EPSILON
  const leafCoordinates = getLeafCoordinates(index, clusterId)
  const bounds = leafCoordinates.length > 0 ? createMapBounds(leafCoordinates) : undefined

  const withProgress = (center: LngLat, zoom: number): ClusterExpansionTarget => ({
    center,
    zoom: Math.max(Math.min(zoom, maxZoom), currentZoom),
  })

  if (!bounds) {
    return withProgress(fallbackCoordinate, expansionZoom)
  }

  const center = getBoundsCenter(bounds)
  const hasSeparableLeaves = getLongitudeSpan(bounds.east, bounds.west) > DEGENERATE_SPAN_DEGREES ||
    bounds.north - bounds.south > DEGENERATE_SPAN_DEGREES

  if (!hasSeparableLeaves || !viewport || viewport.width <= 0 || viewport.height <= 0) {
    return withProgress(center, expansionZoom)
  }

  return withProgress(center, Math.max(expansionZoom, getBoundsFitZoom(bounds, viewport, paddingPx)))
}

export function getElementViewport(element: HTMLElement | null | undefined): ClusterExpansionViewport | undefined {
  if (!element) {
    return undefined
  }

  const width = element.clientWidth
  const height = element.clientHeight

  return width > 0 && height > 0 ? { width, height } : undefined
}
